import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('catch-all-email');

export default function CatchAllEmailPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'A catch-all domain accepts mail addressed to every possible mailbox, including ones that do not exist. Verification works by asking a server whether an address is deliverable, so on a catch-all every answer is yes — which means no verifier can tell a real mailbox from a typo. That is why you get "unknown".',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Why verification returns unknown">
        <p>
          Mailbox verification is an SMTP conversation held up to the point of asking. The verifier
          connects, names a sender, names the recipient, and reads the answer to{' '}
          <Tok>RCPT TO</Tok>:
        </p>
        <LearnCode>
          {`normal domain
RCPT TO:<real.person@company.com>          250 2.1.5 Ok          → deliverable
RCPT TO:<nobody-xyz-9f2@company.com>       550 5.1.1 User unknown → not deliverable

catch-all domain
RCPT TO:<real.person@company.com>          250 2.1.5 Ok          → ?
RCPT TO:<nobody-xyz-9f2@company.com>       250 2.1.5 Ok          → ?`}
        </LearnCode>
        <p>
          On the second domain both answers are identical, so the test carries no information. The
          honest report is <strong className="text-txt">unknown</strong> — and a verifier that
          returns "valid" here is not detecting anything, it is relabelling a 250 it cannot
          interpret.
        </p>
        <p>
          The random-address probe in that example is how the condition is detected at all: ask for
          a mailbox that certainly does not exist, and if the server accepts it, everything it
          accepts is meaningless.
        </p>
      </LearnSection>

      <LearnSection title="Why domains are configured this way">
        <p>
          Almost never to frustrate verification. Four ordinary reasons, and knowing which one
          applies changes how you treat the address:
        </p>
        <LearnTable
          head={['Reason', 'Typical of', 'Are the addresses real?']}
          rows={[
            ['Not losing mail to typos', 'Small businesses, professional services', <Lead key="a">Mostly yes</Lead>],
            ['Default on the hosting plan', 'cPanel and similar shared hosting', 'Mixed — nobody chose this'],
            ['Deliberate address-per-vendor use', 'Technical users, privacy-conscious teams', <Lead key="b">Yes, and each one is single-purpose</Lead>],
            ['Anti-enumeration posture', 'Larger organisations and security-conscious hosts', 'Unknowable by design'],
          ]}
        />
        <p>
          The last row matters more than it used to. Answering <Tok>550</Tok> for non-existent
          mailboxes tells an attacker exactly which addresses are real, which is a directory
          harvesting gift. A number of large hosts now accept everything at SMTP time and reject
          later precisely to close that, so "catch-all" is increasingly a security default rather
          than a configuration choice.
        </p>
      </LearnSection>

      <LearnSection title="Deciding what to do with them">
        <p>
          Deleting every unknown throws away real customers; sending to all of them risks the
          bounces. The workable answer is to treat them as a separate segment and let other signals
          decide.
        </p>
        <LearnTable
          head={['Signal on the address', 'Read it as', 'Action']}
          rows={[
            ['They signed up themselves and confirmed', <Lead key="a">Almost certainly real</Lead>, 'Send normally'],
            ['They have opened or clicked before', <Lead key="b">Real — engagement is proof</Lead>, 'Send normally'],
            ['A named local part (firstname.lastname)', 'Probably real', 'Send, watch bounces'],
            [<span key="c">A <Link to="/learn/role-based-email-addresses" className="text-accent underline underline-offset-4">role address</Link> (info@, sales@)</span>, 'Real, but a shared mailbox', 'Send with care — higher complaint risk'],
            ['Imported from a list you bought', 'Unknowable, and the least of your problems', <Lead key="d">Do not send</Lead>],
            ['No engagement in 12 months', 'Dead or uninterested', 'Suppress'],
          ]}
        />
        <p>
          The pattern underneath: <Lead>engagement history beats verification</Lead>. A prior open
          is direct evidence a human received the message, which is a stronger proof than any SMTP
          probe. Verification only matters where you have no history.
        </p>
      </LearnSection>

      <LearnSection title="The effect on your numbers">
        <LearnSubsection title="Bounce rate understates the problem">
          <p>
            A catch-all accepts at SMTP time, so a dead address there does not produce an immediate
            hard bounce. It either bounces hours later — often not attributed back to the send — or
            is silently discarded. Your{' '}
            <Link to="/learn/email-bounce-rate" className="text-accent underline underline-offset-4">
              bounce rate
            </Link>{' '}
            looks healthy while a chunk of the mail reaches nobody.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Engagement rate is the better instrument">
          <p>
            A segment of catch-all addresses that never opens anything is telling you what the
            bounce rate cannot. If open rate on that segment is near zero while the rest of your
            list is normal, the addresses are not being read — suppress on that evidence rather than
            waiting for bounces that will never arrive.
          </p>
        </LearnSubsection>
        <LearnSubsection title="A high catch-all share is itself a signal">
          <p>
            Twenty percent unknowns on a list built from your own signups is unremarkable. Eighty
            percent means the list came from somewhere else — scraped, purchased, or generated by
            pattern-guessing against company domains, which produces catch-all hits by design.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="What nobody can do">
        <LearnList>
          <li>
            <Lead>Prove a mailbox exists on a catch-all domain.</Lead> The server will not
            distinguish, and there is no other authority to ask. Any vendor claiming otherwise is
            either reporting engagement data they hold from elsewhere, or guessing.
          </li>
          <li>
            <Lead>Reliably use the "deep" SMTP tricks.</Lead> Some verifiers probe further into the
            conversation looking for behavioural differences. It works occasionally, breaks often,
            and looks like directory harvesting to the receiving server — which is a good way to
            get the probing IP blocked.
          </li>
        </LearnList>
        <p>
          If you want to see the conversation yourself rather than take this on trust,{' '}
          <Link to="/tools/smtp-test" className="text-accent underline underline-offset-4">
            our SMTP tool
          </Link>{' '}
          runs it live and shows the full transcript, including the <Tok>RCPT TO</Tok> response that
          decides the whole question.
        </p>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'What does "catch-all" mean in email verification?',
    a: 'It means the receiving server accepts mail for every address at that domain, whether or not the mailbox exists. Verification asks the server whether an address is deliverable, so on a catch-all every answer is yes and the test tells you nothing.',
  },
  {
    q: 'Why does my verifier return "unknown" instead of valid or invalid?',
    a: 'Because unknown is the accurate answer. The server accepted a deliberately random address as readily as the real one, so there is no basis to call it either. A verifier reporting "valid" there is relabelling a response it cannot interpret.',
  },
  {
    q: 'Should I send to catch-all addresses?',
    a: 'It depends entirely on where the address came from. Someone who signed up themselves, or has opened your mail before, is almost certainly real — send. An address from a purchased list is unknowable and should not be sent to, though that is true regardless of catch-all status.',
  },
  {
    q: 'How is a catch-all domain detected?',
    a: 'By asking for a mailbox that certainly does not exist — a long random local part. If the server accepts that, it accepts everything, so every other answer from that domain is uninformative. That single probe is the whole detection method.',
  },
  {
    q: 'Do catch-all addresses hurt my bounce rate?',
    a: 'Less than they should, which is the trap. They accept at SMTP time, so a dead address there bounces late or is silently discarded rather than producing a clean hard bounce. Watch engagement on that segment instead — near-zero opens tell you what bounces will not.',
  },
  {
    q: 'Can any tool prove a catch-all address is real?',
    a: 'No. The receiving server will not distinguish, and there is no other authority to consult. The only genuine evidence is engagement — a prior open or reply proves a human received it, which is stronger than any SMTP probe could ever be.',
  },
];
